import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import FacebookLogin from 'graphqlDocuments/mutations/FacebookLogin.graphql';
import GoogleLogin from 'graphqlDocuments/mutations/GoogleLogin.graphql';
import compose from 'lodash.flowright';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { SignUpPartial, AddNotification, AppleLogin } from 'graphqlDocuments/mutations';
import { withCustomProps } from 'hoc/withCustomProps';
import getBrowserUtmData from 'utils/getBrowserUtmData';
import withCaptcha, { useCaptcha } from 'components/Captcha/withCaptcha';
import SIGN_UP_CAPTCHA_IS_REQUIRED_QUERY from './SignUpCaptchaIsRequiredQuery.graphql';

const DISABLE_KOUNT_ACCESS = process.env.DISABLE_KOUNT_ACCESS || '';

function withApolloCompat(WrappedComponent) {
  return function WithApolloCompatWrapper(props) {
    const [signUpPartialMutation] = useMutation(SignUpPartial);
    const [addNotificationMutation] = useMutation(AddNotification);
    const [facebookLoginMutation] = useMutation(FacebookLogin);
    const [googleLoginMutation] = useMutation(GoogleLogin);
    const [appleLoginMutation] = useMutation(AppleLogin);
    const { data } = useQuery(SIGN_UP_CAPTCHA_IS_REQUIRED_QUERY, {
      fetchPolicy: 'no-cache',
    });

    return (
      <WrappedComponent
        {...props}
        signUpPartial={(variables) => signUpPartialMutation({ variables })}
        addNotification={(variables) => addNotificationMutation({ variables })}
        facebookLogin={(variables) => facebookLoginMutation({ variables })}
        googleLogin={(variables) => googleLoginMutation({ variables })}
        appleLogin={(variables) => appleLoginMutation({ variables })}
        captchaConfig={data?.captchaIsRequired?.signUp}
      />
    );
  };
}

const SignUpContainer = (SignUp) =>
  compose(
    withCustomProps({
      enableKountAccess: !DISABLE_KOUNT_ACCESS || DISABLE_KOUNT_ACCESS.toUpperCase() === 'FALSE',
    }),
    withTranslation(['SIGNUP', 'LOGIN', 'ERRORS']),
    withApolloCompat,
    withCaptcha({ alwaysRequired: true }),
    (Component) =>
      function SignUpContainerWrapper(props) {
        const { captchaConfig } = props;
        const captchaProps = useCaptcha();
        const { captchaShow } = captchaProps;
        useEffect(() => {
          if (captchaConfig) {
            captchaShow(captchaConfig);
          }
        }, [captchaConfig, captchaShow]);
        const utmData = useMemo(getBrowserUtmData, []);
        return <Component {...props} captchaProps={captchaProps} utmData={utmData} />;
      },
    withRouter,
  )(SignUp);

export default SignUpContainer;
